import { Injectable, Inject } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';

import {
  HttpRequest,
  QueryBuilder,
  IQueryResult,
  IQueryFacetInfo,
  IQueryFacetInfoMap,
  IQueryGenericResult,
  IQueryGenericFacets,
  QueryResponse,
} from '@relevance/core';
import { QueryService } from '@relevance/angular-core';

import { SearchServiceConfig, SEARCH_CONFIG } from './search.config';
import { ISearchParams, ISearchFilter, ISearchResponse, ISearchQueryPipe, ISearchApplyPipe } from './search.model';

/**
 * This service provides an easy way to implement semantic/search navigation.
 */
@Injectable()
export class SearchService<R extends IQueryResult = IQueryGenericResult, F extends object = IQueryGenericFacets> {

  private suggester: HttpRequest<IQueryFacetInfo[]> = null;
  private searcher: HttpRequest<QueryResponse<R, F>> = null;

  private params: ISearchParams = {};
  private offsetValue = 0;
  private queryPipe: ISearchQueryPipe = null;
  private applyPipe: ISearchApplyPipe<R, F> = null;

  public response: QueryResponse<R, F> = null;
  public items: R[] = [];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private queryService: QueryService<R, F>,
    @Inject(SEARCH_CONFIG) private config: SearchServiceConfig,
  ) {
    if (this.config.autoContext) {
      this.route.queryParams.subscribe(async (queryParams) => {
        this.params = queryParams as ISearchParams;
        this.apply(true);
      });
    }
  }

  /**
   * Reload the response and results based on the current parameters.
   */
  private reload(): Promise<ISearchResponse<R, F>> {
    if (this.searcher) {
      this.searcher.abort();
    }

    const builder = this.buildQuery();

    return new Promise((resolve, reject) => {
      this.searcher = this.queryService
        .searchQuery(this.config.queryName, builder)
        .then((response) => {
          this.response = response;

          for (const item of response.results) {
            this.items.push(item);
          }

          resolve({
            chunk: response.results,
            items: this.items,
            response,
          });
        })
        .catch((err) => {
          console.error('query error', err);
          reject(err);
        })
        .finally(() => this.searcher = null);
    });
  }

  /**
   * Set a parameter.
   */
  private setParameter(name: string, value: any): void {
    const params: any = { ...this.params };
    params[name] = value;

    const result: any = {};
    for (const key of Object.keys(params)) {
      const val = params[key];
      if (typeof val !== 'undefined' && val !== null) {
        result[key] = val;
      }
    }
    this.params = result;
  }

  /**
   * Get the query service instance.
   */
  public getQueryService(): QueryService<R, F> {
    return this.queryService;
  }

  /**
   * Check if a request is running.
   */
  public isRunning(type?: 'search'|'suggest'): boolean {
    if ((!type || type === 'search') && this.searcher) {
      return true;
    }
    if ((!type || type === 'suggest') && this.suggest) {
      return true;
    }
    return false;
  }

  /**
   * Save the context to the browser's query string.
   */
  public applyContext(params?: ISearchParams): ISearchParams {
    params = params || this.params;

    if (this.config.autoContext) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { ...params },
      });
    }

    return JSON.parse(JSON.stringify(params));
  }

  /**
   * Get the query mode.
   */
  public get mode(): 'filter'|'boost'|'mixed' {
    if (this.config.forceMode) {
      return this.config.forceMode;
    }
    return this.params.m === 'f' ? 'filter' : this.params.m === 'b' ? 'boost' : 'mixed';
  }

  /**
   * Set the query mode.
   */
  public set mode(value: 'filter'|'boost'|'mixed') {
    if (this.config.forceMode) {
      throw new Error('cannot set mode with forceMode');
    }
    this.setParameter('m', value[0]);
  }

  /**
   * Get the query string.
   */
  public get queryString(): string|undefined {
    return this.params.q;
  }

  /**
   * Set the query string.
   */
  public set queryString(value: string|null) {
    this.setParameter('q', value);
  }

  /**
   * Get the list of applied filters.
   */
  public get filters(): ISearchFilter[] {
    const result: ISearchFilter[] = [];

    if (!this.params.f) {
      return result;
    }

    for (let item of this.params.f.split(';')) {
      let negative = false;
      if (item[0] === '!') {
        negative = true;
        item = item.slice(1);
      }

      const parts = item.split(':', 2);
      if (parts.length === 2) {
        result.push({ type: parts[0], value: parts[1], negative });
      }
    }

    return result;
  }

  /**
   * Set the list of applied filters.
   */
  public set filters(items: ISearchFilter[]) {
    const parts = [];

    for (const item of items) {
      parts.push(`${item.negative ? '!' : ''}${item.type}:${item.value}`);
    }

    this.setParameter('f', parts.length > 0 ? parts.join(';') : null);
  }

  /**
   * Get the sorting field.
   */
  public get sortField(): string|undefined {
    return this.params.s;
  }

  /**
   * Set the sorting field.
   */
  public set sortField(value: string|null) {
    this.setParameter('s', value);
    if (!value) {
      this.setParameter('o', null);
    }
  }

  /**
   * Get the sorting order.
   */
  public get sortOrder(): 'asc'|'desc' {
    return this.params.o === 'd' ? 'desc' : 'asc';
  }

  /**
   * Set the sorting order.
   */
  public set sortOrder(value: 'asc'|'desc') {
    this.setParameter('o', value === 'desc' ? 'd' : 'a');
  }

  /**
   * Get the current result offset.
   */
  public get offset(): number {
    return this.offsetValue;
  }

  /**
   * Set the current result offset. Is not available with forceSlice.
   */
  public set offset(value: number|null) {
    if (this.config.forceSlice) {
      throw new Error('cannot set offset with forceSlice');
    }
    this.setParameter('x', value ? value.toString() : undefined);
  }

  /**
   * Get the current result limit.
   */
  public get limit(): number {
    try {
      if (this.config.forceSlice) {
        return this.config.defaultSlice;
      }
      return this.params.y ? parseInt(this.params.y.toString(), 10) : (this.config.defaultSlice || 10);
    } catch (err) {
      return this.config.defaultSlice || 10;
    }
  }

  /**
   * Set the current result limit. Is not available ith forceSlice.
   */
  public set limit(value: number|null) {
    if (this.config.forceSlice) {
      throw new Error('cannot set limit with forceSlice');
    }
    this.setParameter('y', value ? value.toString() : undefined);
  }

  /**
   * Build a query builder object for a specific set of options.
   */
  public buildQuery(params?: ISearchParams, returnOnly?: 'results'|'facets'): QueryBuilder {
    params = params || this.params;

    const builder = new QueryBuilder();
    builder.setOffset(this.offset);

    if (!returnOnly || returnOnly === 'facets') {
      builder.setDefaultFacets(true);
    }

    if (this.mode === 'mixed' || this.mode === 'boost') {
      builder.setMode('boost');
    }

    if (this.queryString) {
      if (this.mode === 'boost') {
        builder.setBoostString(params.q);
      } else {
        builder.setQueryString(params.q);
      }
    }

    for (const filter of this.filters) {
      if (typeof filter.value !== 'undefined') {
        builder.addFilter(filter.type, '==', filter.value, filter.negative);
      }
    }

    if ((!returnOnly || returnOnly === 'results')) {
      builder.setLimit(this.limit);
    } else if (returnOnly === 'facets') {
      builder.setLimit(0);
    }

    if (params.s) {
      if (params.o) {
        builder.replaceSort(params.s, params.o === 'a' ? 'asc' : 'desc');
      } else {
        builder.replaceSort(params.s);
      }
    }

    if (this.queryPipe) {
      this.queryPipe(builder);
    }

    return builder;
  }

  /**
   * Set the query pipe handler.
   */
  public onQuery(handler: ISearchQueryPipe): SearchService<R, F> {
    this.queryPipe = handler;
    return this;
  }

  /**
   * Set the apply pipe handler.
   */
  public onApply(handler: ISearchApplyPipe<R, F>): SearchService<R, F> {
    this.applyPipe = handler;
    return this;
  }

  /**
   * Get facets of a specific type from the response.
   */
  public getFacetsArray(type?: string|string[]): IQueryFacetInfo[] {
    type = typeof type === 'string' ? [type] : type;

    const data = this.getFacetsMap(type);
    const result = [];

    for (const key of Object.keys(data)) {
      for (const item of data[key]) {
        result.push(item);
      }
    }

    return result.sort((a, b) => a.count > b.count ? -1 : a.count < b.count ? 1 : 0);
  }

  /**
   * Get facets map from the response.
   */
  public getFacetsMap(types?: string[]): IQueryFacetInfoMap {
    const rateThreshold = this.config.rateThreshold || 0.005;
    const data = this.response.facetsMap(types);
    const result: IQueryFacetInfoMap = {};
    let min = this.config.countThreshold || 2;

    for (const key of Object.keys(data)) {
      result[key] = [];

      for (const item of data[key]) {
        if (this.hasFilter(item)) {
          continue;
        }

        if (item.count < min) {
          min = item.count;
        }

        const rate = item.count / this.response.count;

        if (rate >= rateThreshold || item.count >= min) {
          result[key].push(item);
        }
      }

      result[key].sort((a, b) => a.count > b.count ? -1 : a.count < b.count ? 1 : 0);
    }

    return result;
  }

  /**
   * Check if a filter is enabled.
   */
  public hasFilter(filter: IQueryFacetInfo|ISearchFilter): boolean {
    for (const item of this.filters) {
      const negative = (filter as any).negative === true;
      if (filter.type === item.type && filter.value === item.value && negative === item.negative) {
        return true;
      }
    }
    return false;
  }

  /**
   * Push a filter.
   */
  public pushFilter(facet: IQueryFacetInfo|ISearchFilter, negative: boolean = false): SearchService<R, F> {
    facet = facet as ISearchFilter;

    const filter = { ...facet, negative };
    if (this.hasFilter(filter)) {
      return;
    }

    let filters = this.filters;
    filters.push(filter);

    if (this.config.stackCount) {
      filters = filters.slice(0 - this.config.stackCount);
    }

    this.filters = filters;
    return this;
  }

  /**
   * Remove a filter.
   */
  public popFilter(filter: ISearchFilter): SearchService<R, F> {
    const filters = [];
    for (const item of this.filters) {
      if (filter.type === item.type && filter.value === item.value && filter.negative === item.negative) {
        continue;
      }
      filters.push(item);
    }
    this.filters = filters;
    return this;
  }

  /**
   * Clear all filters.
   */
  public clearFilters(): SearchService<R, F> {
    this.filters = [];
    return this;
  }

  /**
   * Apply the search.
   */
  public apply(forced: boolean = false): Promise<ISearchResponse<R, F>> {
    if (!forced && this.config.autoContext) {
      this.applyContext();
      return new Promise((resolve) => resolve(null));
    }

    this.items = [];

    if (this.config.forceSlice) {
      this.offsetValue = 0;
    }

    return new Promise((resolve, reject) => {
      this.reload().then((response) => {
        if (this.applyPipe) {
          this.applyPipe(response);
        }
        resolve(response);
      }, reject);
    });
  }

  /**
   * Move to the next result page.
   */
  public nextPage(): Promise<ISearchResponse<R, F>> {
    this.offsetValue += this.limit;
    if (this.config.forceSlice) {
      return this.reload();
    }
    return this.apply();
  }

  /**
   * Perform facet suggestion for a specific query string.
   */
  public suggest(
    text: string,
    type: string|string[] = null,
    count: number = 20,
    ignoreSelected: boolean = false,
  ): Promise<IQueryFacetInfo[]> {
    if (this.suggester) {
      this.suggester.abort();
    }

    text = text.replace(/[^ ]:.*/, '').trim();
    const queryString = text.split(' ').map((v) => v.trim() + '*').join(' AND ');

    const builder = new QueryBuilder()
      .setLimit(0)
      .setQueryString(queryString);

    return new Promise((resolve, reject) => {
      this.suggester = this.queryService
        .searchQuery(this.config.queryName, builder)
        .pipe<IQueryFacetInfo[]>((response) => response.facetsArray())
        .pipe((items) => {
          const value = text.toLowerCase();

          const result = items.sort((a, b) => {
            const avalue = a.value.toString().toLowerCase();
            const bvalue = b.value.toString().toLowerCase();
            let score = 0;
            if (avalue.startsWith(value)) {
              score -= 2;
            }
            if (bvalue.startsWith(value)) {
              score += 2;
            }
            if (avalue.indexOf(value) >= 0) {
              score -= 1;
            }
            if (bvalue.indexOf(value) >= 0) {
              score += 1;
            }
            return score;
          }).slice(0, 20);

          if (!ignoreSelected) {
            return result;
          }

          return result.filter((item) => !this.hasFilter(item));
        })
        .then(resolve)
        .catch((err) => {
          console.error('suggester error', err);
          reject(err);
        })
        .finally(() => this.suggester = null);
    });
  }

  /**
   * Abort any pending request.
   */
  public abort(): void {
    if (this.searcher) {
      this.searcher.abort();
    }
    if (this.suggester) {
      this.suggester.abort();
    }
  }

}
